import React from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { Button } from "antd";
import { FileExcelOutlined } from "@ant-design/icons";
import logoARN from '../../../assets/logos/Banner_Logos_FRES.png';


const FormatoPlanCompras = ({ plan }) => {
    const exportToExcel = async () => {
        try {
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet("Plan de Compras");

            // ✅ Convertir imagen a Base64 y agregarla a Excel
            const imgToBase64 = async (imagePath) => {
                const response = await fetch(imagePath);
                const blob = await response.blob();
                return new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result.split(",")[1]); // Obtener solo Base64
                    reader.readAsDataURL(blob);
                });
            };

            const base64Image = await imgToBase64(logoARN);
            const imageId = workbook.addImage({
                base64: base64Image,
                extension: "png",
            });

            worksheet.addImage(imageId, {
                tl: { col: 0, row: 0 },
                br: { col: 5, row: 7 },
            });

            let currentRow = 8; // 📌 Comienza después de la imagen en fila 5

            // 🔹 Estilos generales
            const headerStyle = {
                font: { bold: true, color: { argb: "FFFFFF" } },
                fill: { type: "pattern", pattern: "solid", fgColor: { argb: "037910" } },
                alignment: { horizontal: "left", vertical: "middle" }
            };

            const subtotalStyle = {
                font: { bold: true },
                fill: { type: "pattern", pattern: "solid", fgColor: { argb: "D9E1F2" } },
                alignment: { horizontal: "right" }
            };

            // 📌 Obtener valores de Cofinanciación ARN y Contrapartida
            const totalProyecto = plan.conceptoInversion.find(c => c.concepto === "TOTAL PROYECTO");
            const subtotalContrapartida = plan.conceptoInversion.find(c => c.concepto === "F. Contrapartida")?.valor || 0;
            const cofinanciacionARN = totalProyecto.valor - subtotalContrapartida;

            // 📌 Cabecera con título y distribución ajustada
            worksheet.mergeCells(`A${currentRow}:F${currentRow}`);
            worksheet.getCell(`A${currentRow}`).value = "PLAN DE COMPRAS";
            worksheet.getCell(`A${currentRow}`).style = headerStyle;
            currentRow++;

            worksheet.getCell(`A${currentRow}`).value = "Código SIR:";
            worksheet.getCell(`B${currentRow}`).value = plan.codSir || "N/A";
            worksheet.getCell(`A${currentRow}`).font = { bold: true };

            worksheet.getCell(`E${currentRow}`).value = "Cofinanciación ARN";
            worksheet.getCell(`F${currentRow}`).value = cofinanciacionARN;
            worksheet.getCell(`E${currentRow}`).style = subtotalStyle;
            worksheet.getCell(`F${currentRow}`).style = subtotalStyle;
            worksheet.getCell(`F${currentRow}`).numFmt = "$#,##0.00";
            worksheet.getCell(`F${currentRow}`).alignment = { horizontal: "right" };



            currentRow++;

            worksheet.getCell(`E${currentRow}`).value = "Contrapartida";
            worksheet.getCell(`F${currentRow}`).value = subtotalContrapartida;
            worksheet.getCell(`E${currentRow}`).style = subtotalStyle;
            worksheet.getCell(`F${currentRow}`).style = subtotalStyle;
            worksheet.getCell(`F${currentRow}`).numFmt = "$#,##0.00";
            worksheet.getCell(`F${currentRow}`).alignment = { horizontal: "right" };

            currentRow += 2;

            // 🔹 Procesar cada sección
            plan.dataItems.forEach((section) => {
                const sectionTitle = section.title;

                worksheet.mergeCells(`A${currentRow}:F${currentRow}`);
                worksheet.getCell(`A${currentRow}`).value = sectionTitle;
                worksheet.getCell(`A${currentRow}`).style = headerStyle;
                worksheet.getCell(`A${currentRow}`).alignment = { horizontal: "left" };
                currentRow++;

                // 📌 Agregar encabezados de productos
                worksheet.addRow([
                    "Descripción", "Recurso de Fortalecimiento ARN", "Unidad de medida",
                    "Cantidad", "Valor Unitario", "Valor Total"
                ]).font = { bold: true };
                currentRow++;

                // 📌 Insertar productos de la sección
                section.items.forEach((item) => {
                    let row = worksheet.addRow([
                        item.nombre_producto,
                        item.recurso,
                        item.nombreUnidadMedida,
                        item.cantidad,
                        parseFloat(item.valorUnitario),
                        parseFloat(item.valorTotal),
                    ]);

                    row.getCell(5).numFmt = "$#,##0.00";
                    row.getCell(6).numFmt = "$#,##0.00";

                    currentRow++;
                });

                // 📌 Buscar el subtotal
                const conceptoInversion = plan.conceptoInversion.find(c => c.concepto === sectionTitle);
                if (conceptoInversion) {
                    worksheet.getCell(`A${currentRow}`).value = `Subtotal ${sectionTitle}:`;
                    worksheet.getCell(`A${currentRow}`).style = subtotalStyle;
                    worksheet.mergeCells(`A${currentRow}:E${currentRow}`);

                    worksheet.getCell(`F${currentRow}`).value = conceptoInversion.valor;
                    worksheet.getCell(`F${currentRow}`).numFmt = "$#,##0.00";
                    worksheet.getCell(`F${currentRow}`).alignment = { horizontal: "right" };
                    worksheet.getCell(`F${currentRow}`).style = subtotalStyle;

                    currentRow += 2;
                }
            });

            // 📌 Sección "CONCEPTO DE LA INVERSIÓN"
            worksheet.getCell(`A${currentRow}`).value = "CONCEPTO DE LA INVERSIÓN";
            worksheet.getCell(`A${currentRow}`).style = headerStyle;
            worksheet.mergeCells(`A${currentRow}:F${currentRow}`);
            currentRow++;

            // 📌 Insertar conceptos de inversión
            plan.conceptoInversion.forEach((concepto) => {
                if (concepto.concepto !== "TOTAL PROYECTO") {
                    worksheet.mergeCells(`A${currentRow}:B${currentRow}`);
                    worksheet.mergeCells(`C${currentRow}:D${currentRow}`);
                    worksheet.mergeCells(`E${currentRow}:F${currentRow}`);

                    worksheet.getCell(`A${currentRow}`).value = concepto.concepto;
                    worksheet.getCell(`C${currentRow}`).value = concepto.valor;
                    worksheet.getCell(`E${currentRow}`).value = concepto.porcentaje;

                    worksheet.getCell(`C${currentRow}`).numFmt = "$#,##0.00";
                    worksheet.getCell(`E${currentRow}`).alignment = { horizontal: "right" };

                    currentRow++;
                }
            });

            // 📌 Agregar "TOTAL PROYECTO"
            if (totalProyecto) {
                worksheet.mergeCells(`A${currentRow}:B${currentRow}`);
                worksheet.mergeCells(`C${currentRow}:D${currentRow}`);
                worksheet.mergeCells(`E${currentRow}:F${currentRow}`);

                worksheet.getCell(`A${currentRow}`).value = "TOTAL PROYECTO:";
                worksheet.getCell(`A${currentRow}`).style = headerStyle;
                worksheet.getCell(`A${currentRow}`).alignment = { horizontal: "left" };

                worksheet.getCell(`C${currentRow}`).value = totalProyecto.valor;
                worksheet.getCell(`C${currentRow}`).numFmt = "$#,##0.00";
                worksheet.getCell(`C${currentRow}`).alignment = { horizontal: "right" };
                worksheet.getCell(`C${currentRow}`).font = { bold: true, color: { argb: "FFFFFF" } }; // 🔹 Texto en blanco
                worksheet.getCell(`C${currentRow}`).fill = {
                    type: "pattern",
                    pattern: "solid",
                    font: { bold: true, color: { argb: "FFFFFF" } },
                    fgColor: { argb: "037910" }
                };

                worksheet.getCell(`E${currentRow}`).font = { bold: true, color: { argb: "FFFFFF" } }; // 🔹 Texto en blanco
                worksheet.getCell(`E${currentRow}`).fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "037910" }
                };


                worksheet.getCell(`E${currentRow}`).value = totalProyecto.porcentaje;
                worksheet.getCell(`E${currentRow}`).alignment = { horizontal: "right" };





                currentRow += 3;
            }

            // 📌 Ajuste de ancho de columnas
            worksheet.columns.forEach(column => {
                let maxLength = 0;
                column.eachCell({ includeEmpty: true }, cell => {
                    const cellValue = cell.value ? cell.value.toString().trim() : "";
                    maxLength = Math.max(maxLength, cellValue.length);
                });

                column.width = Math.min(maxLength + 2, 25);
            });


            const buffer = await workbook.xlsx.writeBuffer();
            saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), "Plan_de_Compras.xlsx");

        } catch (error) {
            console.error("❌ Error al generar el Excel:", error);
        }
    };

    return (
        <Button type="primary" icon={<FileExcelOutlined />} onClick={exportToExcel}>

        </Button>
    );
};

export default FormatoPlanCompras;
